import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { Mail, Lock, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';

const LOGO_URL = "/logo.png";
const MIN_PASSWORD_LENGTH = 6;
const GENERIC_RESET_MESSAGE = 'Si el correo existe, recibirás instrucciones para cambiar tu contraseña.';

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const hasToken = Boolean(token);

  const [email, setEmail] = useState('');
  const [passwords, setPasswords] = useState({ password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (loading || requestSent) return;

    setError('');
    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setRequestSent(true);
      toast.success(GENERIC_RESET_MESSAGE);
    } catch (err) {
      setError('No pudimos procesar la solicitud en este momento. Inténtalo de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError('');

    if (!token) {
      setError('El enlace de recuperación no es válido o expiró.');
      return;
    }

    if (passwords.password !== passwords.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (passwords.password.length < MIN_PASSWORD_LENGTH) {
      setError(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`);
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, passwords.password);
      setSuccess(true);
      toast.success('Contraseña actualizada correctamente');
      window.history.replaceState({}, document.title, '/reset-password');
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.toLowerCase().includes('token')) {
        setError('El enlace de recuperación no es válido o expiró. Solicita uno nuevo.');
      } else if (detail.includes('6 caracteres')) {
        setError(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`);
      } else {
        setError('No pudimos actualizar la contraseña. Inténtalo de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-4">
        <div className="bg-white p-8 rounded-sm border border-[#C3C3C3] max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#3C3C3C] mb-2">
            Contraseña actualizada
          </h2>
          <p className="text-[#5E5E5E] mb-4">
            Tu contraseña fue cambiada correctamente. Serás redirigido al login.
          </p>
          <Link
            to="/login"
            className="text-[#818113] font-medium hover:underline"
          >
            Ir a iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-4" data-testid="reset-password-page">
      <div className="bg-white p-8 rounded-sm border border-[#C3C3C3] max-w-md w-full">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <img
            src={LOGO_URL}
            alt="Olivo Cards"
            className="h-10 w-auto"
          />
        </Link>

        <div className="text-center mb-6">
          <h1
            className="text-2xl font-bold text-[#3C3C3C] mb-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {hasToken ? 'Nueva contraseña' : 'Recuperar contraseña'}
          </h1>
          <p className="text-[#5E5E5E] text-sm">
            {hasToken
              ? 'Ingresa y confirma tu nueva contraseña'
              : 'Ingresa tu correo electrónico para recibir un enlace de recuperación'
            }
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!hasToken ? (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#3C3C3C]">Correo electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A2A2A2]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="pl-10 border-[#C3C3C3] focus:border-[#C5C51E] focus:ring-[#C5C51E]"
                  required
                  disabled={loading || requestSent}
                  data-testid="input-reset-email"
                />
              </div>
            </div>

            {requestSent && (
              <div className="p-4 bg-[#E7E723]/20 border border-[#C5C51E] rounded-sm">
                <p className="text-sm text-[#5E5E5E]">{GENERIC_RESET_MESSAGE}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#C5C51E] hover:bg-[#A3A318] text-black font-semibold"
              disabled={loading || requestSent}
              data-testid="send-reset-link-btn"
            >
              {loading ? 'Enviando...' : requestSent ? 'Solicitud enviada' : 'Enviar enlace'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#3C3C3C]">Nueva contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A2A2A2]" />
                <Input
                  id="password"
                  type="password"
                  value={passwords.password}
                  onChange={(e) => setPasswords({ ...passwords, password: e.target.value })}
                  placeholder="••••••••"
                  className="pl-10 border-[#C3C3C3] focus:border-[#C5C51E] focus:ring-[#C5C51E]"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  data-testid="input-new-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#3C3C3C]">Confirmar contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A2A2A2]" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  className="pl-10 border-[#C3C3C3] focus:border-[#C5C51E] focus:ring-[#C5C51E]"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  data-testid="input-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#C5C51E] hover:bg-[#A3A318] text-black font-semibold"
              disabled={loading}
              data-testid="reset-password-btn"
            >
              {loading ? 'Actualizando...' : 'Actualizar contraseña'}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-[#5E5E5E]">
          <Link
            to="/login"
            className="text-[#818113] font-medium hover:underline flex items-center justify-center gap-2"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a iniciar sesión
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
